import {
  PiCameraDuotone,
  PiCopyDuotone,
  PiShareNetworkDuotone,
  PiBookmarkDuotone,
  PiPencilSimpleDuotone,
  PiNotePencilDuotone,
} from "react-icons/pi";
import { getGeminiCompletion } from "../../ai/GeminiUtils";
import { logAnalyticsEvent } from "../../engagement/Amplitude";
import ResponsiveText from "../../shared/ResponsiveText";
import { handleShare, handleSave } from "../../tasks/utils/TaskUtils";
import { Task } from "../../tasks/models/Task";
import { useNavigate } from "react-router-dom";
import {
  useShowTempErrorMessage,
  useShowTempMessage,
} from "../../shared/hooks/useUtilityContext";
import { useAuth } from "../../authentication/AuthContext";
import * as htmlToImage from "html-to-image";
import { removeLeadingWhitespace } from "../utils/snippets";
import { measureExecutionTime } from "../../shared/utils/TimeUtils";
import { useQueryClient } from "react-query";

export default function SnippetActions({
  snippet,
  code,
  loading,
  id,
  updating,
  setUpdating,
  analyzeSnippet,
  setSnippet,
  selectedLanguage,
  setSelectedLanguage,
  clearSnippet,
  screenshotTitle,
  tags,
}: {
  snippet?: Task;
  code: string;
  loading: boolean;
  id?: string;
  updating: boolean;
  setUpdating: (value: boolean) => void;
  analyzeSnippet: (snippet: string) => Promise<void>;
  setSnippet: (snippet: string) => void;
  selectedLanguage: string;
  setSelectedLanguage: (language: string) => void;
  clearSnippet: () => void;
  screenshotTitle: string;
  tags?: string[];
}) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const showTempErrorMessage = useShowTempErrorMessage();
  const showTempMessage = useShowTempMessage();
  const queryClient = useQueryClient();

  // Function to clean up markdown code blocks
  function cleanMarkdownCodeBlock(update: string): void {
    // Regular expression pattern to match markdown code blocks
    const pattern = /```([a-z]*)\n([\s\S]*?)```/g;

    // Match all code blocks in the markdown update
    const matches = [...update.matchAll(pattern)];

    // If code blocks were found
    if (matches.length > 0) {
      console.log("inside");
      for (const match of matches) {
        console.log("match", match);
        // Extract the language from the first line of the code block
        let language = match[1];

        // Extract the code from the rest of the code block
        const code = match[2].trimEnd();

        // Clean the code by removing the language and any leading or trailing whitespace
        let cleanedCode = code
          .replace(`\n${language}`, "")
          .replaceAll("```", "")
          .trim();

        cleanedCode = removeLeadingWhitespace(cleanedCode);

        // Set the snippet to the cleaned code
        setSnippet(cleanedCode);

        // Set the selected language
        setSelectedLanguage(language);
      }
    } else {
      console.log("outside");

      setSnippet(update);
    }
  }

  const captureScreenshot = async ({
    toClipboard = false,
  }: {
    toClipboard: boolean;
  }) => {
    var node = document.getElementById("screenshot-container");

    const titleElement = document.getElementById("screenshot-title");
    let originalDisplay = "";

    if (titleElement) {
      if (!screenshotTitle) {
        originalDisplay = titleElement.style.display;
        titleElement.style.display = "none";
      }
    }

    htmlToImage
      .toPng(node!, {
        canvasHeight: node!.clientHeight * 3,
        canvasWidth: node!.clientWidth * 3,
      })
      .then(function (dataUrl) {
        var img = new Image();
        img.src = dataUrl;

        if (toClipboard) {
          fetch(dataUrl)
            .then((response) => response.blob())
            .then((blob) => {
              const item = new ClipboardItem({ "image/png": blob });
              navigator.clipboard.write([item]);
              showTempMessage("Copied screenshot to clipboard!");
            })
            .catch((error) =>
              console.error("Failed to copy image to clipboard:", error)
            );
        } else {
          var link = document.createElement("a");
          link.download = "code-snippet.png";
          link.href = dataUrl;
          link.click();
          if (titleElement) {
            titleElement.style.display = originalDisplay;
          }
        }
      })
      .catch(function (error) {
        console.error("oops, something went wrong!", error);
      });
  };

  return (
    <div className="flex flex-col md:flex-row items-center gap-2 mt-4 mb-4 px-4 md:px-0 w-full">
      {id === undefined && (
        <button
          disabled={!code || loading}
          className="btn btn-primary w-full md:w-auto"
          onClick={async () => {
            logAnalyticsEvent("Snippet Submitted");
            await analyzeSnippet(code);
          }}
        >
          {
            <ResponsiveText
              longText="Analyze and Save"
              shortText="Analyze"
            />
          }
        </button>
      )}
      {snippet && (
        <button
          title="Remix Snippet"
          disabled={!code || loading}
          className="btn btn-primary w-full md:w-auto"
          onClick={async () => {
            logAnalyticsEvent("Create new snippet");
            clearSnippet();
            navigate("/snippet/new");
          }}
        >
          <ResponsiveText
            longText="Create a Snippet"
            shortText="+ Snippet"
          />
        </button>
      )}
      {snippet && (
        <button
          disabled={!code || loading}
          className="btn btn-secondary w-full md:w-auto"
          onClick={async () => {
            logAnalyticsEvent("Remix snippet");
            navigate("/snippet/new");
          }}
        >
          Remix
        </button>
      )}
      <div className="flex flex-row">
        <div
          className={`btn btn-ghost btn-square cursor-pointer `}
          onClick={() => {
            logAnalyticsEvent("Capture Screenshot", {
              title: screenshotTitle,
              snippet: code,
              tags: tags,
              id: snippet?.id,
            });
            if (code) {
              captureScreenshot({ toClipboard: false });
            }
          }}
          onContextMenu={(e) => {
            e.preventDefault();
            logAnalyticsEvent("Capture Screenshot", {
              title: screenshotTitle,
              snippet: code,
              tags: tags,
              id: snippet?.id,
            });
            if (code) {
              captureScreenshot({
                toClipboard: true,
              });
            }
          }}
        >
          <PiCameraDuotone
            title="Generate Share Image"
            size={24}
          />
        </div>
        <div
          className="btn btn-square btn-ghost text-base"
          onClick={() => {
            logAnalyticsEvent("Copy Task Snippet");
            navigator.clipboard.writeText(code);
            showTempMessage("Copied snippet to clipboard!");
          }}
        >
          <PiCopyDuotone size={24} />
        </div>
        {snippet && (
          <div className="flex items-center">
            <div className="btn btn-ghost btn-square cursor-pointer ">
              <PiShareNetworkDuotone
                title="Share Snippet"
                onClick={() => {
                  handleShare({
                    task: snippet,
                    user: user!,
                    showTempMessage,
                  });
                }}
                size={24}
              />
            </div>
            <div
              className={`btn btn-ghost btn-square cursor-pointer ${
                snippet.user_saves?.includes(user?.id ?? "")
                  ? "text-primary"
                  : "text-base-content"
              }`}
            >
              <PiBookmarkDuotone
                title="Save Snippet"
                onClick={async () => {
                  await handleSave({
                    task: snippet,
                    user: user!,
                    showTempMessage,
                    queryClient,
                    id: snippet.id!,
                  });
                }}
                size={24}
              />
            </div>
            {snippet.user_id == user?.id && (
              <div className="btn btn-ghost btn-square cursor-pointer">
                <PiPencilSimpleDuotone
                  title="Edit Snippet"
                  onClick={() => {
                    logAnalyticsEvent("Edit Snippet");
                    navigate("/snippet/edit/" + snippet.id + "/");
                  }}
                  size={24}
                />
              </div>
            )}
            {/* <div className="btn btn-ghost btn-square cursor-pointer">
              <PiNotePencilDuotone
                title="Code Post"
                onClick={() => {
                  logAnalyticsEvent("Code Post");
                  navigate("/tools/code-post/" + snippet.id + "/");
                }}
                size={24}
              />
            </div> */}
          </div>
        )}
      </div>
      <div className="dropdown w-full mr-2">
        <div
          tabIndex={0}
          role="button"
          className="btn m-1 w-full"
        >
          {
            <div>
              <div className={`${updating ? "hidden" : ""}`}>☝️ Bartender</div>
              <div
                className={`${updating ? "loading loading-dots" : "hidden"}`}
              >
                🤌
              </div>
            </div>
          }
        </div>
        <ul
          tabIndex={0}
          className="dropdown-content z-[1] menu p-2 shadow bg-base-100 rounded-box w-full"
        >
          <li
            onClick={async () => {
              if (!code || updating) {
                return;
              }
              logAnalyticsEvent("Bartender", {
                type: "Add Comments",
              });
              setUpdating(true);
              const update = await getGeminiCompletion({
                prompt: `Add comments to this code snippet. Do not add additional code: ${code}`,
              });

              cleanMarkdownCodeBlock(update);
              setUpdating(false);
            }}
          >
            <a>📑 Add Comments</a>
          </li>
          <li
            onClick={async () => {
              if (!code || updating) {
                return;
              }
              logAnalyticsEvent("Bartender", {
                type: "Make It Better",
              });
              setUpdating(true);
              const measuredUpdate = await measureExecutionTime(
                getGeminiCompletion
              );

              const update = await measuredUpdate({
                prompt: `Make this code snippet better by simplifying, adding error handling, and making it more readable. Just return the new code: ${code}`,
              });
              cleanMarkdownCodeBlock(update);
              setUpdating(false);
            }}
          >
            <a>🍹 Make It Better</a>
          </li>
          <li
            onClick={async () => {
              if (!code || updating) {
                return;
              }
              logAnalyticsEvent("Bartender", {
                type: "Code Golf",
              });
              setUpdating(true);
              const update = await getGeminiCompletion({
                prompt: `Make this code shorter, as if you were playing code golf. Put each line of code on a new line so the lines are not too long: ${code}`,
              });

              cleanMarkdownCodeBlock(update);
              setUpdating(false);
            }}
          >
            <a>⛳️ Code Golf</a>
          </li>
        </ul>
      </div>
      {/* <div className="flex flex-row items-center justify-end gap-2 w-full">
          {selectedLines && selectedLines.length > 0 && (
            <button
              className="btn btn-outline btn-accent w-full md:w-auto"
              disabled={!snippet || loading}
              onClick={() => {
                setSelectedLines([]);
              }}
            >
              Remove Highlights
            </button>
          )}
        </div>
      </div> */}
    </div>
  );
}
